import * as React from 'react';
import * as classNames from 'classnames';
import {withStyles} from '@material-ui/core/styles';
import FormExplanationText from '@material-ui/core/FormExplanationText';
import {WithStyles} from '@material-ui/core/styles/withStyles';
import FormHelperText from '@material-ui/core/FormHelperText';
import {WrappedFieldMetaProps} from 'redux-form';
import TooltipIcon from '@material-ui/core/TooltipIcon/TooltipIcon';
import {ChildrenRefsContext} from '../FormContext/withChildrenRefsProvider';
import {stringToDashed} from 'cl-automation-utils';
import Tooltip from '@material-ui/core/Tooltip';

export interface FormFieldProps {
  instructionText?: React.ReactNode;
  explanationText?: React.ReactNode;
  explanationTooltip?: boolean;
  instructionPosition?: 'before' | 'after';
  showErrors: boolean;
  touched: boolean;
  error: WrappedFieldMetaProps['error'];
  formHelperText?: React.ReactNode;
  fieldName?: string;
  disabledTooltip?: string;
}

const styles = theme => {
  return {
    fieldContainer: {
      display: 'flex',
      alignItems: 'baseline',
    },
    explanation: {
      marginLeft: theme.spacing(2),
    },
    tooltip: {
      position: 'relative' as 'relative',
      top: theme.spacing(4),
    },
    bottomExplanation: {
      marginTop: 0,
      marginBottom: theme.spacing(3),
    },
  };
};

const FormField = withStyles(styles)((({
  children,
  touched,
  showErrors,
  error,
  instructionText,
  instructionPosition,
  explanationText,
  explanationTooltip,
  classes: {fieldContainer, explanation, tooltip, bottomExplanation},
  formHelperText,
  fieldName,
  disabledTooltip,
}) => {
  const {addChildRef, removeChildRef} = React.useContext(ChildrenRefsContext);
  const callbackRef = React.useCallback(ref => {
    if (ref !== null && addChildRef && fieldName) {
      addChildRef(fieldName, ref);
    }
  }, []);

  React.useEffect(() => {
    if (removeChildRef && fieldName) {
      return () => removeChildRef(fieldName);
    }
  }, []);

  const wrapChildrenWithTooltip = () =>
    disabledTooltip ? <Tooltip title={disabledTooltip}>{children as React.ReactElement}</Tooltip> : children;

  return (
    <div ref={callbackRef} {...(fieldName ? {className: stringToDashed(fieldName)} : {})}>
      {instructionText && instructionPosition === 'before' ? (
        <FormExplanationText>{instructionText}</FormExplanationText>
      ) : (
        undefined
      )}
      {explanationText ? (
        <div className={fieldContainer}>
          {wrapChildrenWithTooltip()}
          {explanationTooltip ? (
            <TooltipIcon variant="inline" className={classNames(explanation, tooltip)} title={explanationText} />
          ) : (
            <FormExplanationText className={explanation}>{explanationText}</FormExplanationText>
          )}
        </div>
      ) : (
        wrapChildrenWithTooltip()
      )}
      {error && (showErrors || touched) ? <FormHelperText error>{error}</FormHelperText> : formHelperText}
      {instructionText && instructionPosition === 'after' ? (
        <FormExplanationText className={bottomExplanation}>{instructionText}</FormExplanationText>
      ) : (
        undefined
      )}
    </div>
  );
}) as React.FunctionComponent<FormFieldProps & WithStyles<typeof styles>>);

FormField.defaultProps = {
  instructionPosition: 'before',
  formHelperText: <FormHelperText />,
};

export default FormField;
